import {useRef} from 'react'
import {css} from '@emotion/react'
import {Button, ConfigProvider, Modal as AntdModal} from 'antd'
import usePointer from '@/hooks/usePointer.mjs'

const cssFooter = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
})

const cssModal = css({
    display: 'flex',
    flexDirection: 'column',
    top: '10vh',
    left: 0,
    right: 0,
    maxHeight: '90vh',
    maxWidth: '90vw',
    paddingBottom: 0,
    margin: 'auto',
    overflow: 'hidden',
    boxShadow: '0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%)',

    '& .ant-modal-content': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
    },

    '& .ant-modal-header': {
        padding: 0,
    },

    '& .ant-modal-body': {
        display: 'grid',
        flexGrow: 1,
        padding: '10px 16px',
        overflow: 'auto',
    },

    '& .ant-modal-close-x': {
        width: '34px',
        height: '34px',
        lineHeight: '34px',
    },
})

const cssTitle = ({
    fontSize: 14,
    lineHeight: 1,
    padding: '10px 16px',
    userSelect: 'none',
})

const Modal = ({
    style,
    cancelButtonProps,
    cancelText = '取消',
    draggable = true,
    footer,
    height,
    leftButtons,
    noCancelButton = false,
    noOkButton = false,
    okButtonProps,
    okText = '确定',
    okType = 'primary',
    overflow = 'auto',
    rightButtons,
    title,
    onCancel,
    onOk,
    ...props
}) => {
    const refElTitle = useRef()
    const refIsFocused = useRef(false)
    const refPos = useRef([0, 0])

    const hideWindow = () => {
        const elContent = refElTitle.current.closest('.ant-modal-content')
        elContent.style.opacity = 0
    }

    const showWindow = () => {
        const elContent = refElTitle.current.closest('.ant-modal-content')
        elContent.style.opacity = 100
    }

    const pointerEventHandlers = usePointer({
        onPointerDown: e => {
            refIsFocused.current = true

            if (e.ctrlKey) {
                hideWindow()
            }
        },

        onPointerUp: e => {
            refIsFocused.current = false

            if (e.ctrlKey) {
                showWindow()
            }
        },

        onSwipeMove: e => {
            if (1 !== e.buttons) {
                return
            }

            const elModal = refElTitle.current.closest('.ant-modal')
            const pos = refPos.current
            pos[0] += e.movementX
            pos[1] += e.movementY
            elModal.style.transform = `translate(${pos[0]}px, ${pos[1]}px)`
        },
    })

    const handleKeyDown = e => {
        if (
            refIsFocused.current &&
            'Control' === e.key
        ) {
            hideWindow()
        }
    }

    const handleKeyUp = e => {
        if (
            refIsFocused.current &&
            'Control' === e.key
        ) {
            showWindow()
        }
    }

    const closable = props.closable ?? false
    const styleModal = {...style, height}
    const styleBody = {overflow}

    const styleTitle = {
        marginRight: closable ? 56 : 0,
        cursor: draggable ? 'move' : '',
    }

    const _title = (
        <div
            ref={refElTitle}
            css={cssTitle}
            style={styleTitle}
            tabIndex="-1"
            {...(draggable ? pointerEventHandlers : null)}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
        >
            {title}
        </div>
    )

    const cancelButton = noCancelButton ?
        null
        :
        (
            <Button
                onClick={onCancel}
                {...cancelButtonProps}
            >
                {cancelText}
            </Button>
        )

    const okButton = noOkButton ?
        null
        :
        (
            <Button
                type={okType}
                onClick={onOk}
                {...okButtonProps}
            >
                {okText}
            </Button>
        )

    const _footer = footer ?? (
        <div css={cssFooter}>
            <ConfigProvider componentSize="small">
                <div>{leftButtons}</div>

                <div>
                    {rightButtons}
                    {cancelButton}
                    {okButton}
                </div>
            </ConfigProvider>
        </div>
    )

    return (
        <AntdModal
            css={cssModal}
            style={styleModal}
            bodyStyle={styleBody}
            mask={false}
            maskClosable={false}
            closable={closable}
            title={_title}
            footer={_footer}
            onCancel={onCancel}
            onOk={onOk}
            {...props}
        />
    )
}

const {
    confirm,
    error,
    info,
    success,
    warning,
} = AntdModal

Object.assign(Modal, {
    confirm,
    error,
    info,
    success,
    warning,
})

export default Modal
